<template>
  <base-modal-component>
    <template v-slot:header>
      <h4 class="u-typography-color md-title">
        Add new ticket banner
      </h4>
    </template>

    <template v-slot:body>
      <p
        v-if="error && error.messages"
        class="md-error"
      >
        {{ error.messages }}
      </p>
      <form
        novalidate
        class="c-banner-form"
      >
        <!-- default banner -->
        <section class="c-banner-form__section">
          <div class="c-banner-form__grid">
            <md-field :class="getValidationClass($v.ticketBannerItem.default.bannerName)">
              <label for="name">Banner name</label>
              <md-input
                v-model="ticketBannerItem.default.bannerName"
                v-focus
                name="bannerName"
                type="text"
                class="md-input"
                autocomplete="off"
              />
              <span
                v-if="!$v.ticketBannerItem.default.bannerName.required"
                class="md-error"
              >Ticket banner name is
                required</span>
            </md-field>
            <md-field :class="getValidationClass($v.ticketBannerItem.default.bannerUrl)">
              <label>Banner URL</label>
              <md-input
                v-model="ticketBannerItem.default.bannerUrl"
                name="bannerUrl"
                type="text"
                class="md-input"
                autocomplete="off"
              />
              <span
                v-if="!$v.ticketBannerItem.default.bannerUrl.required"
                class="md-error"
              >Ticket banner url is
                required</span>
            </md-field>
          </div>
          <error-response-messages :field-name="'bannerName'" />

          <div class="c-banner-form__check">
            <md-checkbox
              v-model="ticketBannerItem.default.isInternalUrl"
              class="md-checkbox"
            />
            <label for="name">Is internal URL</label>
          </div>

          <div class="c-banner-form__image">
            <div class="c-avatar">
              <img
                v-if="ticketBannerUrl() || getTicketBannerImagePath['default']"
                class="c-avatar__img"
                :src="getTicketBannerImagePath['default'] ? getTicketBannerImagePath['default'] : ticketBannerUrl()"
                :disabled="!bannerToUpload['default']"
                alt="ticket-banner"
              >
              <span
                v-else
                class="c-avatar__placeholder"
              >
                <app-icon
                  name="add_photo_alternate"
                  :size="28"
                />
                <strong>Upload image</strong>
                <small>Click to choose a file</small>
              </span>
              <input
                type="file"
                name="imagePath"
                class="c-avatar__input"
                accept="image/*"
                @change="handleTicketBannerChange($event.target.files)"
              >
            </div>
            <div class="c-banner-form__sizes">
              <md-field :class="getValidationClass($v.ticketBannerItem.default.width)">
                <label>Width (px)</label>
                <md-input
                  v-model="ticketBannerItem.default.width"
                  name="width"
                  type="number"
                  min="1"
                  class="md-input"
                  autocomplete="off"
                  :disabled="!bannerToUpload['default']"
                />
                <span
                  v-if="$v.ticketBannerItem.default.width.$invalid"
                  class="md-error"
                >
                  Width is required and must be a number greater than 0
                </span>
              </md-field>
              <md-field :class="getValidationClass($v.ticketBannerItem.default.height)">
                <label>Height (px)</label>
                <md-input
                  v-model="ticketBannerItem.default.height"
                  name="height"
                  type="number"
                  min="1"
                  class="md-input"
                  autocomplete="off"
                  :disabled="!bannerToUpload['default']"
                />
                <span
                  v-if="$v.ticketBannerItem.default.height.$invalid"
                  class="md-error"
                >
                  Height is required and must be a number greater than 0
                </span>
              </md-field>
              <md-field :class="getValidationClass($v.ticketBannerItem.default.quality)">
                <label>Quality (%)</label>
                <md-input
                  v-model="ticketBannerItem.default.quality"
                  name="quality"
                  type="number"
                  min="1"
                  max="100"
                  class="md-input"
                  autocomplete="off"
                  :disabled="!bannerToUpload['default']"
                />
                <span
                  v-if="$v.ticketBannerItem.default.quality.$invalid"
                  class="md-error"
                >
                  Quality is required and must be a number between 1 and 100
                </span>
              </md-field>
              <p class="c-banner-form__hint">
                Choose a new image to set its width, height and quality. They are applied when the new image is uploaded, and existing images keep their saved values.
              </p>
            </div>
          </div>
        </section>

        <!-- per-language overrides -->
        <section class="c-banner-form__section c-banner-form__section--languages">
          <div class="c-banner-form__section-head">
            <h5>Language overrides</h5>
            <p>Optional URL and image per language.</p>
          </div>
          <md-tabs
            class="c-tabs c-tabs--small"
            :md-active-tab="currentLanguage"
          >
            <md-tab
              v-for="language in languages"
              :id="language"
              :key="language"
              :md-label="language"
              @click="changeLanguage(language)"
            />
          </md-tabs>
          <div
            v-for="language in languages"
            :key="language"
          >
            <div v-if="language === currentLanguage">
              <md-field :class="getValidationClass($v.ticketBannerItem[language].bannerUrl)">
                <label>Banner URL</label>
                <md-input
                  v-model="ticketBannerItem[language].bannerUrl"
                  name="bannerUrl"
                  type="text"
                  class="md-input"
                  autocomplete="off"
                />
              </md-field>
              <div class="c-banner-form__check">
                <md-checkbox
                  v-model="ticketBannerItem[language].isInternalUrl"
                  class="md-checkbox"
                />
                <label for="name">Is internal URL</label>
              </div>
              <div class="c-banner-form__image">
                <div :class="['c-avatar']">
                  <img
                    v-if="ticketBannerUrl(language) || getTicketBannerImagePath[language]"
                    class="c-avatar__img"
                    :src="getTicketBannerImagePath[language] ? getTicketBannerImagePath[language] : ticketBannerUrl(language)"
                    :disabled="!bannerToUpload[language]"
                    alt="ticket-banner"
                  >
                  <span
                    v-else
                    class="c-avatar__placeholder"
                  >
                    <app-icon
                      name="add_photo_alternate"
                      :size="28"
                    />
                    <strong>Upload image</strong>
                    <small>Click to choose a file</small>
                  </span>
                  <input
                    ref="file"
                    type="file"
                    name="imagePath"
                    class="c-avatar__input"
                    accept="image/*"
                    @change="handleTicketBannerChange($event.target.files, language)"
                  >
                  <div>
                    <generic-button
                      v-if="ticketBannerUrl(language)"
                      class="c-avatar__button"
                      variation="red"
                      @click="handleTicketBannerDelete(language)"
                    >
                      Delete
                    </generic-button>
                  </div>
                </div>
                <div class="c-banner-form__sizes">
                  <md-field :class="getValidationClass($v.ticketBannerItem[language].width)">
                    <label>Width (px)</label>
                    <md-input
                      v-model="ticketBannerItem[language].width"
                      name="width"
                      type="number"
                      min="1"
                      class="md-input"
                      autocomplete="off"
                      :disabled="!bannerToUpload[language]"
                    />
                    <span
                      v-if="$v.ticketBannerItem[language].width.$invalid"
                      class="md-error"
                    >
                      Width is required and must be a number greater than 0
                    </span>
                  </md-field>
                  <md-field :class="getValidationClass($v.ticketBannerItem[language].height)">
                    <label>Height (px)</label>
                    <md-input
                      v-model="ticketBannerItem[language].height"
                      name="height"
                      type="number"
                      min="1"
                      class="md-input"
                      autocomplete="off"
                      :disabled="!bannerToUpload[language]"
                    />
                    <span
                      v-if="$v.ticketBannerItem[language].height.$invalid"
                      class="md-error"
                    >
                      Width is required and must be a number greater than 0
                    </span>
                  </md-field>
                  <md-field :class="getValidationClass($v.ticketBannerItem[language].quality)">
                    <label>Quality (%)</label>
                    <md-input
                      v-model="ticketBannerItem[language].quality"
                      name="quality"
                      type="number"
                      min="1"
                      max="100"
                      class="md-input"
                      autocomplete="off"
                      :disabled="!bannerToUpload[language]"
                    />
                    <span
                      v-if="$v.ticketBannerItem[language].quality.$invalid"
                      class="md-error"
                    >
                      Quality is required and must be a number between 1 and 100
                    </span>
                  </md-field>
                  <p class="c-banner-form__hint">
                    Choose a new image to set its width, height and quality. They are applied when the new image is uploaded, and existing images keep their saved values.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>
      </form>
    </template>
    <template v-slot:footer>
      <div class="u-mr-xs">
        <generic-button
          variation="grey"
          @click="close"
        >
          Close
        </generic-button>
      </div>
      <generic-button
        variation="red"
        @click="validateForm"
      >
        Submit
      </generic-button>
    </template>
  </base-modal-component>
</template>

<script>
import {mapActions, mapGetters} from "vuex";
import {required, numeric, minValue, between} from "vuelidate/lib/validators";
import GenericButton from "@/shared/buttons/GenericButton";
import AppIcon from "@/shared/app-icon/AppIcon";
import ErrorResponseMessages from "@/shared/error-response-messages/ErrorResponseMessages";
import BaseModalComponent from "@/shared/modal/BaseModalComponent";
import {validationBaseMixin} from "@/mixins/ValidationBaseMixin";
import {logoFileExtensions} from "../../../../const/index";
import {checkFileExtensions} from "../../../../const/validator";
import {configurationsService} from "../../../../services/configurations";
import config from "../../../../../config/local";
import {responseService} from "../../../../services/response";
import get from "lodash/get";
import forEach from "lodash/forEach";
import map from "lodash/map";
import isArray from "lodash/isArray";

export default {
  name: "CreateTicketBannerItemModal",
  components: {
    AppIcon,
    GenericButton,
    ErrorResponseMessages,
    BaseModalComponent,
  },
  mixins: [validationBaseMixin],
  props: {
    ticketBannerItem: {
      type: Object,
      required: true,
    },
    successFunction: {
      type: Function,
      required: true,
    },
    languages: {
      type: Array,
      required: true
    },
  },

  data() {
    return {
      messages: {
        error: null,
        success: null,
      },
      bannerFileError: null,
      bannerUploadFileExtensions: logoFileExtensions,
      previewTicketBannerUrl: this.languagesForBannerUrls(),
      bannerToUpload: this.languagesForBannerUrls(),
      currentLanguage: this.languages[0],
      formIsInvalid: false
    };
  },

  validations() {
    const createValidations = (langCode) => {
      const hasImageChange = this.bannerToUpload[langCode]

      return hasImageChange ?
        {
          bannerName: langCode === 'default' ? { required } : {},
          bannerUrl: { required },
          width: { numeric, required, minValue: minValue(1) },
          height: { numeric, required, minValue: minValue(1) },
          quality: { numeric, required, between: between(1, 100) },
        } :
        {
          bannerName: {},
          bannerUrl: {},
          width: {},
          height: {},
          quality: {},
        }
    };

    const validations = {};

    validations['default'] = createValidations('default');
    for (const langKey in this.languages) {
      const langCode = this.languages[langKey]
      validations[langCode] = createValidations(langCode);
    }

    return {
      ticketBannerItem: validations,
    };
  },

  computed: {
    ...mapGetters("response", ["success", "error"]),

    getTicketBannerImagePath() {
      return this.previewTicketBannerUrl;
    },
  },

  methods: {
    ...mapActions("response", [
      "handleSuccess",
      "handleError",
      "resetError",
      "setGlobalError",
    ]),

    ticketBannerUrl(lang = 'default') {
      const imageUrl = get(this.ticketBannerItem[lang], "imagePath", '');
      return imageUrl && `${config.BASE_URL}/${imageUrl}`;
    },

    async validateForm() {
      this.$v.$touch();

      if (this.$v.$invalid || this.isInvalidImageForm()) {
        return
      }
      try {
        await this.saveBannerImages();
        this.successFunction(this.ticketBannerItem);
        this.close();
      } catch (error) {
        let formattedError = error.data.message

        if (isArray(error.data.message)) {
          formattedError = error.data.message.reduce((acc, message) => {
            return `${acc}${message}\n\r`
          }, '')
        }

        this.setGlobalError(formattedError);
      }
    },

    isInvalidImageForm() {
      this.formIsInvalid = false;
      const defaultIsValid = !!(this.bannerToUpload['default'] || this.ticketBannerUrl('default'));

      if (!defaultIsValid) {
        this.setGlobalError("Default Image is required.");
        this.formIsInvalid = true;
        return this.formIsInvalid
      }
    },

    close() {
      this.$emit("close");
      this.resetError();
    },

    handleTicketBannerChange(files, language = 'default') {
      this.validateImageFile(files.item(0));
      if (this.imageFileError) {
        this.setGlobalError("Only image files are allowed. (jpg, jpeg, png and gif)");
        return;
      }
      this.bannerToUpload[language] = files.item(0);
      this.$refs.file[0].value = '';
      const reader = new FileReader();
      reader.readAsDataURL(this.bannerToUpload[language]);
      reader.onload = (event) => {
        this.previewTicketBannerUrl[language] = event.target.result;
        this.resetValidations();
      };

      this.resetValidations();
    },

    async handleTicketBannerDelete(language) {
      try {
        const imageName = this.ticketBannerItem[language].imagePath.split('/').pop();
        await configurationsService
          .deleteLanguageTicketBanner(imageName)
          .then(() => {
            this.ticketBannerItem[language].imagePath = '';
            this.previewTicketBannerUrl[language] = '';
            this.successFunction(this.ticketBannerItem);
          });
        this.handleSuccess({message: `Ticket banner for ${language} deleted successfully.`});
      } catch (error) {
        this.handleError(responseService.getErrorMessage(error.data.message));
      }

    },

    validateImageFile(value) {
      this.imageFileError = checkFileExtensions(
        value,
        this.bannerUploadFileExtensions
      );
    },

    languagesForBannerUrls() {
      let previewBannerUrls = {default: ''};
      forEach(this.languages, lang => previewBannerUrls[lang] = '')

      return previewBannerUrls;
    },

    async saveBannerImages() {
      return Promise.all(map(Object.keys(this.bannerToUpload), (banner) => {
        if (this.bannerToUpload[banner]) {
          return this.handleTicketBannerUpdate(banner)
        }

        return Promise.resolve(true);
      }));
    },

    async handleTicketBannerUpdate(language = 'default') {
      const formData = new FormData();
      formData.append("file", this.bannerToUpload[language], this.bannerToUpload[language].name);
      formData.append("width", +this.ticketBannerItem[language].width);
      formData.append("height", +this.ticketBannerItem[language].height);
      formData.append("quality", +this.ticketBannerItem[language].quality);

      this.resetError();
      await configurationsService
        .saveTicketBannerImage(formData)
        .then((res) => {
          this.ticketBannerItem[language].imagePath = res.data.image.imagePath
        });
      this.handleSuccess({message: "Ticket banner uploaded successfully."});
      this.bannerToUpload[language] = null;
      this.resetValidations();
    },

    changeLanguage(language) {
      this.currentLanguage = language;
    },

    resetValidations() {
      this.$v.$reset();
    }
  },
};
</script>

<style lang="scss" scoped>
.c-banner-form {
  display: flex;
  flex-direction: column;
  gap: 24px;
  font-family: var(--font-sans);

  .md-field {
    margin: 0 0 4px;

    &.md-invalid {
      margin-bottom: 24px;
    }
  }
}

.c-banner-form__section {
  display: flex;
  flex-direction: column;
  gap: 16px;

  &--languages {
    padding: 16px 20px 20px;

    // one wrapper per language is rendered, only the selected one has content;
    // empty ones must not take part in the flex gap (it added ~16px per language)
    > div:empty {
      display: none;
    }

    // URL, checkbox and image rows of the selected language
    > div > div {
      display: flex;
      flex-direction: column;
      gap: 16px;
    }

    border: 1px solid var(--border-default);
    border-radius: var(--radius-lg);
    background-color: var(--bg-subtle);

    ::v-deep .c-tabs--small {
      border-bottom: none !important;

      > .md-tabs-navigation {
        padding: 0;
        background-color: transparent !important;
      }
    }
  }
}

.c-banner-form__section-head {
  h5 {
    margin: 0;
    font-size: 14px;
    font-weight: 600;
    color: var(--text-primary);
  }

  p {
    margin: 2px 0 0;
    font-size: 13px;
    color: var(--text-secondary);
  }
}

.c-banner-form__grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
}

// checkbox + its label on one line
.c-banner-form__check {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: -4px;

  .md-checkbox {
    margin: 0;
  }

  label {
    margin: 0;
    font-size: 14px;
    color: var(--text-primary);
  }
}

// image upload box + size settings beside it
.c-banner-form__image {
  display: grid;
  grid-template-columns: 200px minmax(0, 1fr);
  gap: 20px;
  align-items: start;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
}

.c-banner-form__sizes {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0 12px;
  align-content: start;

  // narrow columns: the validation messages wrap to up to 3 lines, so reserve that room under the box
  .md-field.md-invalid {
    margin-bottom: 56px;

    ::v-deep .md-error {
      line-height: 1.35;
    }
  }
}

.c-banner-form__hint {
  grid-column: 1 / -1;
  margin: 0;
  font-size: 12px;
  color: var(--text-muted);
}

// the whole box is the (invisible) file input; this only draws it
.c-avatar {
  width: 200px;
  height: 160px;
  min-height: 0;
  margin: 0;
  overflow: visible;
  border: 1px dashed var(--border-strong);
  border-radius: var(--radius-lg);
  background-color: var(--bg-subtle) !important;
  transition: border-color var(--duration-fast) ease, background-color var(--duration-fast) ease;

  &:hover {
    border-color: var(--brand-600);
    background-color: var(--brand-50) !important;
  }

  // "Replace image" overlay only when an image is shown
  &:after {
    content: 'Replace image';
    width: auto;
    padding: 6px 10px;
    border-radius: var(--radius-full);
    background-color: rgba(16, 24, 40, 0.75);
    color: #fff !important;
    font-size: 12px;
    font-weight: 600;
    white-space: nowrap;
  }

  &:not(:has(.c-avatar__img)):after {
    display: none;
  }

  &:focus-within {
    border-color: var(--brand-600);
    box-shadow: var(--focus-ring);
  }
}

.c-avatar__img {
  width: 100%;
  height: 100%;
  max-height: 100%;
  padding: 8px;
  border-radius: var(--radius-lg);
}

.c-avatar__placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  padding: 0 12px;
  text-align: center;
  color: var(--text-secondary);
  pointer-events: none;

  .c-app-icon {
    margin-bottom: 4px;
    color: var(--brand-600);
  }

  strong {
    font-size: 13px;
    font-weight: 600;
    color: var(--text-primary);
  }

  small {
    font-size: 12px;
    color: var(--text-muted);
  }
}

// "Delete" for a language image: small, top-right corner of the box
::v-deep .c-avatar__button {
  top: 8px;
  right: 8px;
  bottom: auto !important;
  z-index: 3;
  transform: scale(0.85);
  transform-origin: top right;
}
</style>
